import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleFormField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import type { SubjectKind } from '../api/oversight-contracts';
import { definitionsApi } from '../api/definitions-api';
import type {
  IBankQuestionRow,
  IBankQuestionWrite,
  IOptionListRow,
  IVocabulary
} from '../api/definitions-contracts';
import {
  AdminPage,
  LoadError,
  BUILDER_TABS,
  TableSkeleton,
  useAdminData
} from '../components/admin-page';
import { refusalMessage } from '../refusal';

/**
 * The shared question bank — `question` rows, survey-agnostic, keyed by a slug like
 * `partners.has_partner`. Real as of SR-123 ticket 3: `GET/POST/PUT/DELETE
 * /admin/definitions/questions`.
 *
 * The one thing this screen has to get across, and the reason the banner below is not
 * boilerplate: **editing a bank row does not change a published survey.** Publish
 * materializes every default onto the `question_usage` (data-model design §3.5), so a
 * published version stopped reading these rows the moment it froze. An edit here reaches the
 * next publish.
 *
 * ## The column the fixture had and the wire does not
 *
 * "Placed in" is gone. The fixture invented a `placements` array; `QuestionDto` carries no
 * such thing, and deriving it would mean reading every version document — 593 questions
 * against a document that runs to ~110 KB each. The survey filter went with it, since it
 * filtered on exactly that. What replaced it is the filter the server really has: `?code=` is
 * a **code prefix**, which is how the reserved `fa.` / `ria.` / `fsp.` / `teams.` namespaces
 * are addressed anyway.
 *
 * The label filter stays client-side, over the whole fetched list. The endpoint has no label
 * filter at all, and 593 rows is one small response — a box that quietly searched only what
 * a prefix had already narrowed would answer a different question than it appears to.
 */
export function AdminQuestionsPage() {
  const [editing, setEditing] = React.useState<Nullable<IBankQuestionRow>>(null);
  const [creating, setCreating] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [prefix, setPrefix] = React.useState(ALL);

  const { data: questions, error, setError, reload } = useAdminData<IBankQuestionRow[]>(
    (signal) => definitionsApi.questions(prefix === ALL ? undefined : prefix, signal),
    [prefix]
  );
  const { data: vocabulary } = useAdminData<IVocabulary>(() => definitionsApi.vocabulary());
  const { data: optionLists } = useAdminData<IOptionListRow[]>((signal) =>
    definitionsApi.optionLists(undefined, signal)
  );

  const visible = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return questions ?? [];
    return (questions ?? []).filter(
      (question) =>
        question.code.toLowerCase().includes(needle) ||
        question.defaultLabel.toLowerCase().includes(needle)
    );
  }, [questions, search]);

  return (
    <AdminPage title="Survey builder" tabs={BUILDER_TABS}>
      <Alert>
        <AlertTitle>Bank edits reach the next publish, not the live survey</AlertTitle>
        <AlertDescription>
          A published version carries its own materialized copy of every label, field type and
          option list, so nothing here can change a survey a respondent is filling. Editing a
          bank row changes what the next publish of a draft version will take.
        </AlertDescription>
      </Alert>

      {error && <LoadError message={error} />}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Search code or label"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-sm"
          aria-label="Search questions"
        />
        <Select value={prefix} onValueChange={setPrefix}>
          <SelectTrigger className="w-56" aria-label="Filter by code prefix">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Every prefix</SelectItem>
            {RESERVED_PREFIXES.map((candidate) => (
              <SelectItem key={candidate} value={candidate}>
                {candidate}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {visible.length} of {questions?.length ?? 0}
        </span>
        <Button className="ml-auto" onClick={() => setCreating(true)}>
          <Plus />
          New question
        </Button>
      </div>

      {questions === null ? (
        <TableSkeleton />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Default label</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Field type</TableHead>
                <TableHead>Option list</TableHead>
                <TableHead>Origin</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.slice(0, PAGE).map((question) => (
                <TableRow
                  key={question.questionId}
                  className="cursor-pointer"
                  onClick={() => setEditing(question)}
                >
                  <TableCell className="font-mono text-xs">{question.code}</TableCell>
                  <TableCell className="max-w-md">{question.defaultLabel}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {question.subjectKind}
                  </TableCell>
                  <TableCell className="text-xs">{question.fieldType}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {question.defaultOptionList ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="soft">{question.origin}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm">
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    No bank question matches this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {visible.length > PAGE && (
        // Not a pager: the endpoint returns the whole set in one response and takes no page
        // parameter, so there is nothing to fetch a second page of. This is a render cap —
        // 593 rows in one table is slow to paint and useless to read — and it says so rather
        // than looking like a result count.
        <p className="text-sm text-muted-foreground">
          Showing the first {PAGE}. Narrow the search to see the rest — the endpoint answers
          with the whole bank in one read, so this is a rendering limit, not a page.
        </p>
      )}

      <QuestionEditor
        question={editing}
        creating={creating}
        vocabulary={vocabulary}
        optionLists={optionLists ?? []}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSaved={() => {
          setEditing(null);
          setCreating(false);
          void reload();
        }}
        onFailed={setError}
      />
    </AdminPage>
  );
}

const ALL = 'all';
const PAGE = 200;

/** The namespaces `ReservedCodes` holds for the four code-owned seeders. The API refuses a
 *  write to one of these, which is exactly why they are worth being able to browse. */
const RESERVED_PREFIXES = ['ria.', 'fa.', 'fsp.', 'teams.'];

/**
 * One bank row, created or edited.
 *
 * The write is **whole-block**: what this form sends replaces the row, so every field is
 * present in the body even when untouched. `code` and `origin` are read and ignored on an
 * edit — the route's code is the identity and origin is immutable — which is what keeps a
 * read document a legal write body.
 */
function QuestionEditor({
  question,
  creating,
  vocabulary,
  optionLists,
  onClose,
  onSaved,
  onFailed
}: {
  question: Nullable<IBankQuestionRow>;
  creating: boolean;
  vocabulary: Nullable<IVocabulary>;
  optionLists: IOptionListRow[];
  onClose: () => void;
  onSaved: () => void;
  onFailed: (message: string) => void;
}) {
  const open = question !== null || creating;

  const [code, setCode] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [subjectKind, setSubjectKind] = React.useState<SubjectKind>('person');
  const [fieldType, setFieldType] = React.useState('');
  const [optionList, setOptionList] = React.useState(NO_OPTION_LIST);
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setCode(question?.code ?? '');
    setLabel(question?.defaultLabel ?? '');
    setSubjectKind(question?.subjectKind ?? 'person');
    setFieldType(question?.fieldType ?? '');
    setOptionList(question?.defaultOptionList ?? NO_OPTION_LIST);
    setNotes(question?.notes ?? '');
  }, [question, creating]);

  const save = async () => {
    setSaving(true);
    const body: IBankQuestionWrite = {
      code: code.trim(),
      subjectKind,
      fieldType,
      defaultLabel: label,
      defaultOptionList: optionList === NO_OPTION_LIST ? null : optionList,
      notes: notes.trim() === '' ? null : notes
    };
    try {
      if (question) await definitionsApi.updateQuestion(question.code, body);
      else await definitionsApi.createQuestion(body);
      onSaved();
    } catch (saveError) {
      onFailed(refusalMessage(saveError, 'Could not save this question'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!question) return;
    setSaving(true);
    try {
      await definitionsApi.deleteQuestion(question.code);
      onSaved();
    } catch (deleteError) {
      // A 409 here is `code-in-use`: some version still places this question. That is the
      // useful message, and `refusalMessage` surfaces the server's own wording for it.
      onFailed(refusalMessage(deleteError, 'Could not delete this question'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">
            {question?.code ?? 'New bank question'}
          </DialogTitle>
          <DialogDescription>
            The bank’s working copy. A published version keeps its own frozen copy of these
            defaults.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {creating && (
            <SimpleFormField label="Code" htmlFor="question-code" required>
              <Input
                id="question-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="area.thing_asked"
                className="font-mono"
              />
            </SimpleFormField>
          )}

          <SimpleFormField label="Default label" htmlFor="question-label" required>
            <Input
              id="question-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </SimpleFormField>

          <SimpleFormField label="Subject" htmlFor="question-subject">
            <Select
              value={subjectKind}
              onValueChange={(next) => setSubjectKind(next as SubjectKind)}
            >
              <SelectTrigger id="question-subject">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(vocabulary?.enums.subjectKind ?? []).map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {kind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SimpleFormField>

          <SimpleFormField label="Field type" htmlFor="question-type">
            <Select value={fieldType} onValueChange={setFieldType}>
              <SelectTrigger id="question-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* From the vocabulary, not a hardcoded list: it is the same closed set the
                    publish validator checks against, so this select cannot offer a value
                    that would fail at publish. */}
                {(vocabulary?.fieldTypes ?? []).map((type) => (
                  <SelectItem key={type.code} value={type.code}>
                    {type.code}
                    {type.needsOptionPin ? ' · needs an option list' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SimpleFormField>

          <SimpleFormField label="Default option list" htmlFor="question-options">
            <Select value={optionList} onValueChange={setOptionList}>
              <SelectTrigger id="question-options">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_OPTION_LIST}>None — not a choice type</SelectItem>
                {optionLists.map((list) => (
                  <SelectItem key={list.optionListId} value={list.code}>
                    {list.code} · {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SimpleFormField>

          <SimpleFormField label="Analyst notes" htmlFor="question-notes">
            <Textarea
              id="question-notes"
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </SimpleFormField>
        </DialogBody>

        <DialogFooter>
          {question && (
            <Button
              variant="outline"
              className="mr-auto text-destructive"
              onClick={() => void remove()}
              disabled={saving}
            >
              <Trash2 />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || label.trim() === '' || fieldType === ''}
          >
            {saving ? 'Saving…' : 'Save to the bank'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A flag with no list is "not a choice type"; `Select` needs a value to say that with. */
const NO_OPTION_LIST = 'none';
